import React from 'react';

import classNames from 'classnames';

import { money } from './overviewHelpers';

/**
 * IB-699 — Overview KPI flow: Procurable budget → In procurement plan (open procurements) →
 * Still to be planned (dark) → Outstanding to procure (blue, = uncommitted umbrella) → Committed.
 * Committed + In-plan + Still reconcile to the procurable budget; Outstanding = In-plan + Still.
 */
const KpiCard = ({ title, value, subtitle, variant = 'default' }) => {
    const isColoured = variant === 'dark' || variant === 'blue';

    return (
        <div
            className={classNames('tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-px-4 tw-py-4', {
                'tw-border-2 tw-border-grey tw-bg-white': variant === 'default',
                'tw-bg-charcoal tw-text-white': variant === 'dark',
                'tw-bg-blue tw-text-white': variant === 'blue',
            })}
        >
            <div className={classNames('tw-text-sm tw-font-medium', isColoured ? 'tw-text-white tw-opacity-80' : 'tw-text-charcoal-75')}>{title}</div>
            <div className={classNames('tw-text-2.5xl tw-font-semibold tw-leading-none', isColoured ? 'tw-text-white' : 'tw-text-charcoal')}>{value}</div>
            {subtitle && <div className={classNames('tw-text-xs', isColoured ? 'tw-text-white tw-opacity-80' : 'tw-text-charcoal-50')}>{subtitle}</div>}
        </div>
    );
};

const percentOf = (part, whole) => {
    const total = Number(whole) || 0;
    if (total <= 0) {
        return 0;
    }
    return Math.round(((Number(part) || 0) / total) * 100);
};

const KpiFlow = ({ stats, currencySymbol }) => {
    const procurable = stats?.total_non_workforce_budget ?? 0;
    const inPlan = stats?.in_plan_value ?? 0;
    const stillToPlan = stats?.still_to_plan_value ?? 0;
    const outstanding = stats?.outstanding_value ?? 0;
    const committed = stats?.committed_value ?? 0;

    return (
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-3">
            <KpiCard title={$t('Procurable budget')} value={money(currencySymbol, procurable)} subtitle={$t('non-workforce, from Budget')} />
            <KpiCard
                title={$t('In procurement plan')}
                value={money(currencySymbol, inPlan)}
                subtitle={`${percentOf(inPlan, procurable)}% ${$t('of budget in open procurements')}`}
            />
            <KpiCard
                title={$t('Still to be planned')}
                value={money(currencySymbol, stillToPlan)}
                subtitle={`${percentOf(stillToPlan, procurable)}% — ${$t('not yet a procurement')}`}
                variant="dark"
            />
            <KpiCard
                title={$t('Outstanding to procure')}
                value={money(currencySymbol, outstanding)}
                subtitle={$t('uncommitted, ex-workforce (in plan + still to plan)')}
                variant="blue"
            />
            <KpiCard title={$t('Committed')} value={money(currencySymbol, committed)} subtitle={$t('committed to suppliers')} />
        </div>
    );
};

export default KpiFlow;
